import { useRef, useState } from 'react';

import { useBusinessEntity } from '@zenbusiness/application-commons-business-entity';
import { useLinkPressedCallback, useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useQuery, useMutation, gql } from '@zenbusiness/application-commons-graphql';
import Text from '@zenbusiness/common-components/Text';
import Title from '@zenbusiness/common-components/Title';
import { useParams, Navigate, Link as RouterLink } from 'react-router-dom';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import FormHelperText from '@mui/material/FormHelperText';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import { styled } from '@mui/material/styles';
import TextField from '@mui/material/TextField';

import Breadcrumbs from '#client/components/Breadcrumbs';
import empty from '#client/utils/empty';
import exists from '#client/utils/exists';
import states from '#client/utils/states';
import { getErrorsByField, throwExceptions } from '#client/utils/validation';

export const DOMAIN_CONTACT_MUTATE_READ = gql`
  query DomainContactRead($domainContactUuid: UUID!) {
    domainContact(domainContactUuid: $domainContactUuid) {
      domainContactUuid
      contactType
      firstName
      lastName
      orgName
      address1
      address2
      city
      state
      zipCode
      # country
      phone
      fax
      email
      editable
      updatedDatetime
    }
  }
`;

export const DOMAIN_CONTACT_MUTATE_UPDATE = gql`
  mutation DomainContactUpdate($input: UpdateDomainContactAndCopyToInput!) {
    updateDomainContactAndCopyTo(input: $input) {
      domainContacts {
        domainContactUuid
        contactType
        firstName
        lastName
        orgName
        address1
        address2
        city
        state
        zipCode
        # country
        phone
        fax
        email
        editable
        updatedDatetime
      }
    }
  }
`;

const messages = {
  config: {
    owner: 'Owner',
    admin: 'Administrative',
    billing: 'Billing',
    tech: 'Technical',
    firstName: 'First Name',
    lastName: 'Last Name',
    orgName: 'Company Name',
    address1: 'Address 1',
    address2: 'Address 2',
    city: 'City',
    state: 'State',
    country: 'Country',
    zipCode: 'Postal Code',
    email: 'Email',
    phone: 'Phone',
    fax: 'Fax'
  },
  validation: {
    generic: 'Error',
    firstName: {
      minLength: 'First Name may not be empty',
      maxLength: 'First Name must be under 50 characters'
    },
    lastName: {
      minLength: 'Last Name may not be empty',
      maxLength: 'Last Name must be under 50 characters'
    },
    orgName: {
      minLength: 'Organization Name may not be empty',
      maxLength: 'Organization Name must be under 50 characters'
    },
    address1: {
      minLength: 'Address 1 may not be empty',
      maxLength: 'Address must be under 64 characters'
    },
    city: {
      minLength: 'City may not be empty',
      maxLength: 'City must be under 64 characters'
    },
    state: {
      minLength: 'State may not be empty',
      maxLength: 'State must be a valid abbrivation'
    },
    // country: {
    //   minLength: 'Country may not be empty',
    //   maxLength: 'Country must be a valid abbrivation'
    // },
    zipCode: {
      minLength: 'Postal Code may not be empty',
      maxLength: 'Postal Code must be under 16 characters'
    },
    email: {
      format: 'Email must be a valid format',
      minLength: 'Email may not be empty',
      maxLength: 'Email must be under 64 characters'
    },
    phone: {
      minLength: 'Phone may not be empty',
      maxLength: 'Phone must be under 20 characters'
    },
    fax: {
      maxLength: 'Fax must be under 20 characters'
    }
  }
};

const recordPropsByType = [
  {
    key: 'firstName',
    maxLength: 50,
    required: true
  }, {
    key: 'lastName',
    maxLength: 50,
    required: true
  }, {
    key: 'orgName',
    maxLength: 50,
    required: true
  }, {
    key: 'address1',
    maxLength: 64,
    required: true
  }, {
    key: 'address2',
    maxLength: 64,
    required: false
  }, {
    key: 'city',
    maxLength: 64,
    required: true
  }, {
    key: 'state',
    maxLength: 2,
    required: true
  },
  // , {
  //   key: 'country',
  //   maxLength: 2,
  //   required: true
  // }
  {
    key: 'zipCode',
    maxLength: 16,
    required: true
  }, {
    key: 'email',
    maxLength: 64,
    required: true
  }, {
    key: 'phone',
    maxLength: 20,
    required: true
  }, {
    key: 'fax',
    maxLength: 20,
    required: false
  }
];

const StyledBoxRoot = styled(Box)(({ theme }) => ({
  paddingTop: theme.spacing(2),
  '& .MuiTextField-root': {
    width: '100%'
  },
  '& .MuiFormControl-root': {
    width: '100%'
  },
  '& .MuiButton-root': {
    [theme.breakpoints.down('sm')]: {
      width: '100%'
    }
  },
  '& .MuiCheckbox-root': {
    color: theme.palette.primary.focus30
  },
  '& .MuiCheckbox-colorPrimary.Mui-checked': {
    color: theme.palette.primary.main
  }
}));

const StyledTextHeader = styled(Text)(({ theme }) => ({
  marginBottom: theme.spacing(3),
  paddingBottom: theme.spacing(2),
  borderBottom: `1px solid ${theme.palette.primary.focus30}`
}));

const StyledSelectInput = styled(Select)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: '100%',
  borderRadius: theme.spacing(2),
  '& .MuiSelect-icon': {
    top: 'inherit'
  },
  '& .MuiInputBase-formControl': {
    borderRadius: theme.spacing(2)
  }
}));

const StyledTextFieldInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1),
  width: '100%',
  borderRadius: theme.spacing(2),
  '& .MuiSelect-icon': {
    top: 'inherit'
  },
  '& .MuiInputBase-formControl': {
    borderRadius: theme.spacing(2)
  }
}));

const StyledButton = styled(Button)(({ theme }) => ({
  width: '100%',
  height: theme.spacing(7),
  textTransform: 'capitalize',
  '&:disabled': {
    background: theme.palette.primary.focus30
  }
}));

const StyledBoxTitleSelection = styled(Box)(({ theme }) => ({
  marginTop: theme.spacing(2),
  marginBottom: theme.spacing(6)
}));

const StyledGridSection = styled(Grid)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const StyledBoxSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4)
}));

const StyledActionGrid = styled(Grid)(({ theme }) => ({
  marginTop: theme.spacing(2),
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column-reverse'
  }
}));

const DomainContactMutate = () => {
  const { businessEntityUuid } = useBusinessEntity();
  const { domainUuid, domainContactUuid } = useParams();
  const {
    loading: readLoading,
    error: readError,
    data: readData
  } = useQuery(DOMAIN_CONTACT_MUTATE_READ, {
    variables: {
      domainContactUuid
    }
  });

  const [update, {
    loading: updateLoading,
    error: updateError,
    data: updateData
  }] = useMutation(DOMAIN_CONTACT_MUTATE_UPDATE);

  const [copyTo, setCopyTo] = useState(false);

  const inputLabel = useRef(null);

  const loading = readLoading || updateLoading;

  throwExceptions(readError || updateError);

  const contact = readLoading ? {} : (
    (updateData && updateData.domainContact) || (readData && readData.domainContact)
  );

  const [changes, setChanges] = useState({});

  const handleOverviewBreadcrumb = useLinkPressedCallback({
    elementName: 'overview_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_contacts'
  });

  const handleDomainBreadcrumb = useLinkPressedCallback({
    elementName: 'domain_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_contacts'
  });

  const handleDomainsBreadcrumb = useLinkPressedCallback({
    elementName: 'domains_breadcrumb',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_contacts'
  });

  const handleRecordChange = (event) => {
    const {
      name,
      value
    } = event.target;

    setChanges({
      ...changes,
      [name]: value
    });
  };

  const handleCopyTo = () => {
    setCopyTo(!copyTo);
  };

  const unsavedChanges = !empty(changes);
  const unsaved = unsavedChanges || copyTo;

  const handleSaveClick = useButtonPressedCallback({
    elementName: 'save_dns',
    elementText: 'Save',
    pageCategory: 'dashboard',
    pageSection: 'growth',
    screenName: 'dns_contacts'
  });

  const handleContactSave = () => {
    if (!loading && unsaved) {
      update({
        variables: {
          input: {
            domainUuid,
            domainContactUuid,
            data: {
              ...changes,
              copyTo
            }
          }
        }
      });
    }
    handleSaveClick();
  };
  if (!updateError && updateData) {
    return (<Navigate to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}#contacts`} />);
  }
  // Process server validation errors
  const errorsByField = getErrorsByField(updateError);

  // Handle dynamic record configuration fields
  const inputByField = {};
  recordPropsByType.forEach(({ key, required, maxLength }) => {
    const existingValue = ((exists(contact) && exists(contact[key])) ? contact[key] : '');
    const changedValue = (exists(changes[key]) ? changes[key] : null);
    const errorKey = exists(errorsByField[key]) && errorsByField[key][0];
    const value = exists(changedValue) ? changedValue : existingValue;
    let error = null;
    if (messages.validation[key] && errorKey) {
      error = messages.validation[key][errorKey] || messages.validation.generic;
    }
    if (key === 'state') {
      inputByField[key] = (
        <FormControl size="medium" variant="outlined" component="fieldset" error={!!error}>
          <InputLabel ref={inputLabel} id="state-select-label" required={required}>
            {messages.config[key]}
          </InputLabel>
          <StyledSelectInput
            labelId="state-select-label"
            id="state-select"
            key={key}
            name={key}
            value={value}
            onChange={handleRecordChange}
            renderValue={(abbr) => (states[abbr])}
          >
            {Object.entries(states).map(([abbr, name]) => (
              <MenuItem key={abbr} value={abbr}>{name}</MenuItem>
            ))}
          </StyledSelectInput>
          {error && (
            <FormHelperText>{error}</FormHelperText>
          )}
        </FormControl>
      );
      return;
    }
    inputByField[key] = (
      <StyledTextFieldInput
        key={key}
        id={key}
        variant="outlined"
        size="medium"
        label={messages.config[key]}
        name={key}
        value={value}
        onChange={handleRecordChange}
        helperText={error}
        error={!!error}
        required={required}
        inputProps={{
          maxLength
        }}
      />
    );
  });

  const otherContactType = contact.contactType === 'admin' ? 'owner' : 'admin';

  return (
    <StyledBoxRoot>
      <Breadcrumbs gutterBottom>
        <Button variant="text" color="primary" component={RouterLink} onClick={handleOverviewBreadcrumb} to={`/d/company/${businessEntityUuid}/marketing-tools`}>Overview</Button>
        <Button variant="text" color="primary" component={RouterLink} onClick={handleDomainsBreadcrumb} to={`/d/company/${businessEntityUuid}/marketing-tools/domains`}>Domains</Button>
        <Button variant="text" color="primary" component={RouterLink} onClick={handleDomainBreadcrumb} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>Domain</Button>
        <Button variant="text" disabled color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}/contacts/${domainContactUuid}`}>Contact</Button>
      </Breadcrumbs>

      <StyledBoxTitleSelection>
        <Title level={1} variant="lg-alt">
          {`Edit ${contact.contactType ? messages.config[contact.contactType] : ''} Contact`}
        </Title>
      </StyledBoxTitleSelection>

      <div>
        <form autoComplete="on">
          <StyledGridSection
            container
            item
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={3}
            xs
          >
            <Grid item xs={12}>
              <StyledTextHeader bold size="md" color="focus60">Contact Information</StyledTextHeader>
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.firstName}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.lastName}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.orgName}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.phone}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.fax}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.email}
            </Grid>
          </StyledGridSection>
          <StyledGridSection
            container
            item
            direction="row"
            justifyContent="flex-start"
            alignItems="flex-start"
            spacing={3}
            xs
          >
            <Grid item xs={12}>
              <StyledTextHeader bold size="md" color="focus60">Contact Address</StyledTextHeader>
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.address1}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.address2}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.city}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.state}
            </Grid>
            <Grid item xs={12} md={6}>
              {inputByField.zipCode}
            </Grid>
            {/* <Grid item xs={12}>
              {inputByField.country}
            </Grid> */}
          </StyledGridSection>
          <Divider />
          <StyledBoxSection>
            <FormControl component="fieldset">
              <FormGroup>
                <FormControlLabel
                  control={(
                    <Checkbox
                      color="primary"
                      name="copyTo"
                      value={copyTo}
                      onChange={handleCopyTo}
                    />
                  )}
                  label={(
                    <Text size="md" color="focus70">{`Copy contact to ${messages.config[otherContactType]} Contact`}</Text>
                  )}
                />
              </FormGroup>
            </FormControl>
          </StyledBoxSection>
          <StyledActionGrid
            container
            item
            xs
            spacing={3}
          >
            <Grid item xs={12} md={6}>
              <StyledButton variant="outlined" color="primary" component={RouterLink} to={`/d/company/${businessEntityUuid}/marketing-tools/domains/${domainUuid}`}>cancel</StyledButton>
            </Grid>
            <Grid item xs={12} md={6}>
              <StyledButton variant="contained" color="primary" onClick={handleContactSave} disabled={loading || !unsaved}>save</StyledButton>
            </Grid>
          </StyledActionGrid>
        </form>
      </div>
    </StyledBoxRoot>
  );
};

export default DomainContactMutate;
